
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';
import { KnowledgeDocument } from '../types';

/**
 * Validates the JSON structure for imported knowledge documents
 */
export const validateImportJson = (jsonData: any): { valid: boolean; message: string } => {
  if (!Array.isArray(jsonData)) {
    return { valid: false, message: 'Imported data must be an array of documents' };
  }
  
  for (let i = 0; i < jsonData.length; i++) {
    const doc = jsonData[i];
    
    if (!doc.title) {
      return { valid: false, message: `Document at index ${i} is missing a title` };
    }
    
    if (!doc.document?.content) {
      return { valid: false, message: `Document at index ${i} is missing content` };
    }
    
    // Check for required metadata
    if (!doc.metadata?.language) {
      return { valid: false, message: `Document at index ${i} is missing language metadata` };
    }
    
    if (!doc.metadata?.topic) {
      return { valid: false, message: `Document at index ${i} is missing topic metadata` };
    }
  }
  
  return { valid: true, message: 'Validation successful' };
};

/**
 * Formats document for export
 */
export const formatDocumentsForExport = (docs: KnowledgeDocument[]): any[] => {
  return docs.map(doc => ({
    title: doc.title,
    document: doc.document,
    metadata: doc.metadata
  }));
};

/**
 * Generates a JSON file download
 */
export const downloadJsonFile = (data: any, filename: string) => {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  
  // Cleanup
  setTimeout(() => {
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, 100);
};

/**
 * Processes a batch of documents with embeddings generation
 * Returns progress information
 */
export const processBatchWithEmbeddings = async (
  documents: any[],
  generateEmbedding: (text: string) => Promise<number[] | null>,
  progressCallback: (current: number, total: number) => void
): Promise<{ success: number; failed: number; errors: string[] }> => {
  let successCount = 0;
  let failedCount = 0;
  const errors: string[] = [];
  
  for (let i = 0; i < documents.length; i++) {
    try {
      const doc = documents[i];
      
      // Generate embedding for this document
      const text = `${doc.title}\n\n${doc.document.content}`;
      const embedding = await generateEmbedding(text);
      
      if (!embedding) {
        throw new Error(`Failed to generate embedding for document: ${doc.title}`);
      }
      
      // Ensure language is set
      const language = doc.metadata?.language || 'en';
      const isMultiLanguage = doc.metadata?.is_multi_language || false;
      const sourceLanguage = isMultiLanguage ? (doc.metadata?.source_language || language) : language;
      
      // Process tags if they exist
      const tags = doc.metadata?.tags || [];
      
      // Prepare document with metadata
      const documentToSave = {
        title: doc.title,
        document: doc.document,
        metadata: {
          language: language,
          topic: doc.metadata?.topic || 'guide',
          tags: tags,
          is_multi_language: isMultiLanguage,
          source_language: sourceLanguage
        },
        vector: embedding
      };
      
      // Insert document into database
      const { error } = await supabase
        .from('assistant_knowledge')
        .insert(documentToSave);
        
      if (error) {
        throw error;
      }
      
      successCount++;
    } catch (error) {
      failedCount++;
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      errors.push(`Error with document #${i + 1}: ${errorMessage}`);
      console.error('Error processing document:', error);
    }
    
    // Update progress
    progressCallback(i + 1, documents.length);
  }
  
  return { success: successCount, failed: failedCount, errors };
};
